/**
 * useFlightReplay — animates a track's historical flight path on a CesiumJS viewer.
 *
 * Fetches track history, renders the full flight path as a polyline, and animates
 * a drone marker along the path using requestAnimationFrame. Integrates with the
 * mapStore playback state (isPlaying, playbackSpeed, playbackTime).
 *
 * Pattern follows useDronetagCloudLayer.ts.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Viewer as CesiumViewer,
  CustomDataSource,
  Cartesian3,
  Color,
  NearFarScalar,
  VerticalOrigin,
  HeightReference,
  Math as CesiumMath,
  ConstantProperty,
  JulianDate,
  PolylineGlowMaterialProperty,
  PolylineDashMaterialProperty,
} from 'cesium';
import { tracksApi } from '../services/api';
import { useMapStore } from '../store/mapStore';
import {
  DRONE_ICON_DATA_URI,
  PILOT_ICON_DATA_URI,
  HOME_POINT_ICON_DATA_URI,
} from '../components/map/icons';

const DATASOURCE_NAME = 'flight-replay';
const UI_UPDATE_INTERVAL = 250; // Update playbackTime in store every 250ms for UI scrubber

interface TrackPosition {
  timestamp: string | Date;
  latitude: number;
  longitude: number;
  altitudeMSL: number;
  groundSpeed: number;
  heading: number;
  metadata?: Record<string, unknown>;
}

const EMPTY_POSITIONS: TrackPosition[] = [];

export interface FlightReplayState {
  isLoading: boolean;
  error: string | null;
  duration: number; // total flight duration in seconds
  positionCount: number;
}

/**
 * Linear interpolation between two track positions.
 */
function interpolatePosition(
  p1: TrackPosition,
  p2: TrackPosition,
  t: number, // 0-1 progress between p1 and p2
): { latitude: number; longitude: number; altitudeMSL: number; heading: number } {
  return {
    latitude: p1.latitude + (p2.latitude - p1.latitude) * t,
    longitude: p1.longitude + (p2.longitude - p1.longitude) * t,
    altitudeMSL: p1.altitudeMSL + (p2.altitudeMSL - p1.altitudeMSL) * t,
    heading: p1.heading + (p2.heading - p1.heading) * t,
  };
}

/**
 * Find the two positions surrounding a given time, and the interpolation factor.
 */
function findPositionAtTime(
  positions: TrackPosition[],
  currentTime: number,
): { p1: TrackPosition; p2: TrackPosition; t: number } | null {
  if (positions.length < 2) return null;

  for (let i = 0; i < positions.length - 1; i++) {
    const t1 = new Date(positions[i].timestamp).getTime();
    const t2 = new Date(positions[i + 1].timestamp).getTime();

    if (currentTime >= t1 && currentTime <= t2) {
      const t = t2 === t1 ? 0 : (currentTime - t1) / (t2 - t1);
      return { p1: positions[i], p2: positions[i + 1], t };
    }
  }

  // If past the end, clamp to last position
  const lastIdx = positions.length - 1;
  if (currentTime >= new Date(positions[lastIdx].timestamp).getTime()) {
    return { p1: positions[lastIdx], p2: positions[lastIdx], t: 0 };
  }

  // If before the start, clamp to first
  return { p1: positions[0], p2: positions[0], t: 0 };
}

/**
 * Scan track positions (most recent first) for operator/pilot GPS coordinates
 * stored in metadata by the DUMP adapter. Returns the last-known pilot location,
 * or null if no position was ever recorded. This handles the common case where
 * tele-operator messages arrive intermittently — not every track row will have
 * operator coords, but at least one usually does.
 */
function findLastOperatorPosition(
  positions: TrackPosition[],
): { latitude: number; longitude: number; altitude?: number } | null {
  // Walk backward to find the most recent operator position
  for (let i = positions.length - 1; i >= 0; i--) {
    const meta = positions[i].metadata;
    if (
      meta &&
      typeof meta.operatorLatitude === 'number' &&
      typeof meta.operatorLongitude === 'number' &&
      // Skip zero-position (no GPS fix on pilot device)
      !(meta.operatorLatitude === 0 && meta.operatorLongitude === 0)
    ) {
      return {
        latitude: meta.operatorLatitude,
        longitude: meta.operatorLongitude,
        altitude: typeof meta.operatorAltitudeMSL === 'number' ? meta.operatorAltitudeMSL : undefined,
      };
    }
  }
  return null;
}

export function useFlightReplay(
  viewer: CesiumViewer | null,
  trackId: string | null,
  visible: boolean,
): FlightReplayState {
  const dsRef = useRef<CustomDataSource | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  const lastUIUpdateRef = useRef<number>(0);
  const currentPositionRef = useRef<Cartesian3 | null>(null);
  const hasFlewToStartRef = useRef(false);
  const [error] = useState<string | null>(null);
  const setReplayIconMarkers = useMapStore((s) => s.setReplayIconMarkers);

  // Compute a wide time window for fetching history (last 90 days)
  const timeWindow = useRef({
    startTime: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    endTime: new Date().toISOString(),
  });

  // Fetch track history
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['flight-replay-history', trackId],
    queryFn: async () => {
      const response = await tracksApi.getTrackHistory(
        trackId!,
        timeWindow.current.startTime,
        timeWindow.current.endTime,
        2000,
      );
      return response.data;
    },
    enabled: !!trackId && visible,
    staleTime: 5 * 60 * 1000,
  });

  const positions: TrackPosition[] = historyData?.positions || EMPTY_POSITIONS;
  // Drone serial number is returned on the history envelope so the replay
  // tooltip can show a compact identity. Falls back to trackId suffix for
  // sources without identity (radar-only).
  const serialNumber: string | undefined = historyData?.serialNumber;
  // Identity line shared by the marker tooltip and replay icon state.
  const idLine: string =
    serialNumber && serialNumber.length >= 4
      ? serialNumber.slice(-4).toUpperCase()
      : trackId && trackId.length >= 4
        ? trackId.slice(-4).toUpperCase()
        : 'REPLAY';
  const duration =
    positions.length >= 2
      ? (new Date(positions[positions.length - 1].timestamp).getTime() -
          new Date(positions[0].timestamp).getTime()) /
        1000
      : 0;

  // Set up time range when data loads
  useEffect(() => {
    if (positions.length < 2 || !visible) return;

    const startDate = new Date(positions[0].timestamp);
    const endDate = new Date(positions[positions.length - 1].timestamp);
    const store = useMapStore.getState();

    store.setTimeRange({ start: startDate, end: endDate });
    store.setPlaybackTime(startDate);
    store.setPlayback(false);
    hasFlewToStartRef.current = false;
  }, [positions, visible]);

  const publishReplayIconMarkers = useCallback(
    (
      drone: { latitude: number; longitude: number; altitudeMSL: number },
      pilot?: { latitude: number; longitude: number; altitude?: number } | null,
    ) => {
      if (positions.length < 1) return;
      const home = positions[0];
      const pilotMarker = pilot ?? findLastOperatorPosition(positions);

      setReplayIconMarkers([
        {
          id: 'replay-drone',
          kind: 'DRONE',
          name: idLine,
          latitude: drone.latitude,
          longitude: drone.longitude,
          altitudeMsl: drone.altitudeMSL,
          detail: 'Replay track',
          trackId,
        },
        {
          id: 'replay-home',
          kind: 'HOME',
          name: 'Home point',
          latitude: home.latitude,
          longitude: home.longitude,
          altitudeMsl: home.altitudeMSL,
          detail: 'Takeoff point',
          trackId,
        },
        ...(pilotMarker
          ? [
              {
                id: 'replay-pilot',
                kind: 'PILOT' as const,
                name: 'Pilot',
                latitude: pilotMarker.latitude,
                longitude: pilotMarker.longitude,
                altitudeMsl: pilotMarker.altitude ?? null,
                detail: idLine,
                trackId,
              },
            ]
          : []),
      ]);
    },
    [idLine, positions, setReplayIconMarkers, trackId],
  );

  // Build entities on viewer when data loads
  const setupEntities = useCallback(
    (v: CesiumViewer, pos: TrackPosition[]) => {
      if (!v || v.isDestroyed() || pos.length < 2) return;

      // Create or reuse data source
      let ds = dsRef.current;
      if (!ds) {
        ds = new CustomDataSource(DATASOURCE_NAME);
        dsRef.current = ds;
        v.dataSources.add(ds);
      }
      ds.entities.removeAll();
      ds.show = true;

      // Flight path polyline (full trail — dashed, semi-transparent)
      const pathPositions = pos.map((p) =>
        Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitudeMSL),
      );

      // Full flight-path polyline — glow material at the same width/alpha
      // as the live-detection trail (CesiumMap.tsx) so replay visually
      // matches live. The brighter completed-trail polyline (added below)
      // overlays this as the playback progresses.
      ds.entities.add({
        id: 'flight-path',
        polyline: {
          positions: pathPositions,
          width: 4,
          material: new PolylineGlowMaterialProperty({
            glowPower: 0.15,
            color: Color.fromCssColorString('#ffb547').withAlpha(0.85),
          }),
          clampToGround: false,
        },
      });

      // Completed trail polyline (solid, updates during playback)
      const initialPos = Cartesian3.fromDegrees(
        pos[0].longitude,
        pos[0].latitude,
        pos[0].altitudeMSL,
      );
      currentPositionRef.current = initialPos;

      ds.entities.add({
        id: 'completed-trail',
        polyline: {
          // This will be updated dynamically during playback
          positions: [initialPos],
          width: 4,
          material: Color.fromCssColorString('#ffb547').withAlpha(0.8),
          clampToGround: false,
        },
      });

      // Drone marker entity — billboard icon matching live detection style.
      // Details open via DOM click targets instead of Cesium labels.
      const initialHeading = pos[0].heading || 0;
      ds.entities.add({
        id: 'drone-marker',
        position: initialPos,
        billboard: {
          image: DRONE_ICON_DATA_URI,
          width: 54,
          height: 54,
          heightReference: HeightReference.NONE,
          scaleByDistance: new NearFarScalar(500, 1.0, 80000, 0.4),
          rotation: -CesiumMath.toRadians(initialHeading),
          alignedAxis: Cartesian3.UNIT_Z,
        },
      });

      // Home point (takeoff) — billboard icon clamped to the ground so the
      // marker sits on terrain. END is still a simple dot since landing
      // location is less operationally interesting than takeoff origin.
      ds.entities.add({
        id: 'start-marker',
        position: pathPositions[0],
        billboard: {
          image: HOME_POINT_ICON_DATA_URI,
          width: 48,
          height: 48,
          heightReference: HeightReference.CLAMP_TO_GROUND,
          verticalOrigin: VerticalOrigin.BOTTOM,
          scaleByDistance: new NearFarScalar(500, 1.0, 80000, 0.4),
        },
      });

      ds.entities.add({
        id: 'end-marker',
        position: pathPositions[pathPositions.length - 1],
        point: {
          pixelSize: 8,
          color: Color.fromCssColorString('#e63b4d'),
          outlineColor: Color.WHITE,
          outlineWidth: 1,
        },
      });

      // Operator (pilot) position marker — rendered from track metadata.
      // The DUMP adapter stores pilot GPS from tele-operator messages as
      // operatorLatitude/operatorLongitude in the track metadata. We scan
      // all positions and use the last one that has operator coords, since
      // pilot position updates may not be on every telemetry row.
      const operatorPos = findLastOperatorPosition(pos);
      if (operatorPos) {
        const pilotCartesian = Cartesian3.fromDegrees(
          operatorPos.longitude,
          operatorPos.latitude,
          operatorPos.altitude ?? 0,
        );

        // Pilot marker — billboard icon matching live detection style.
        // verticalOrigin=BOTTOM so the bottom of the SVG (the person's
        // feet) sits on the terrain; default CENTER put the body's
        // midpoint at ground level so the legs rendered underground.
        ds.entities.add({
          id: 'operator-marker',
          position: pilotCartesian,
          billboard: {
            image: PILOT_ICON_DATA_URI,
            width: 45,
            height: 45,
            heightReference: HeightReference.CLAMP_TO_GROUND,
            verticalOrigin: VerticalOrigin.BOTTOM,
            scaleByDistance: new NearFarScalar(500, 1.0, 80000, 0.4),
          },
        });

        // Dashed line connecting drone to pilot
        ds.entities.add({
          id: 'drone-pilot-link',
          polyline: {
            positions: [initialPos, pilotCartesian],
            width: 2,
            material: new PolylineDashMaterialProperty({
              color: Color.fromCssColorString('#f59e0b').withAlpha(0.5),
              dashLength: 12,
              dashPattern: 255,
            }),
            clampToGround: false,
          },
        });
      }

      publishReplayIconMarkers(pos[0], operatorPos);

      // Fly camera to start position
      if (!hasFlewToStartRef.current) {
        hasFlewToStartRef.current = true;
        v.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            pos[0].longitude,
            pos[0].latitude,
            Math.max(pos[0].altitudeMSL + 2000, 3000),
          ),
          duration: 1.5,
        });
      }
    },
    [publishReplayIconMarkers],
  );

  // Set up entities when data loads
  useEffect(() => {
    if (!viewer || viewer.isDestroyed() || !visible || positions.length < 2) {
      const store = useMapStore.getState();
      if (store.replayIconMarkers.length > 0) {
        store.setReplayIconMarkers([]);
      }
      return;
    }
    setupEntities(viewer, positions);
  }, [viewer, visible, positions, setupEntities]);

  // Animation loop
  useEffect(() => {
    if (!viewer || viewer.isDestroyed() || !visible || positions.length < 2) return;

    const animate = (timestamp: number) => {
      if (!dsRef.current || !viewer || viewer.isDestroyed()) return;

      const store = useMapStore.getState();
      const { isPlaying, playbackSpeed, playbackTime, timeRange } = store;

      if (isPlaying && playbackTime && timeRange) {
        // Calculate time delta
        const deltaMs = lastFrameRef.current > 0 ? timestamp - lastFrameRef.current : 16;
        lastFrameRef.current = timestamp;

        // Advance playback time
        const newTime = new Date(playbackTime.getTime() + deltaMs * playbackSpeed);
        const endTime = timeRange.end.getTime();

        if (newTime.getTime() >= endTime) {
          // Reached end of track
          store.setPlaybackTime(timeRange.end);
          store.setPlayback(false);
          lastFrameRef.current = 0;
        } else {
          // Update store at throttled rate for UI
          if (timestamp - lastUIUpdateRef.current > UI_UPDATE_INTERVAL) {
            store.setPlaybackTime(newTime);
            lastUIUpdateRef.current = timestamp;
          }

          // Interpolate position
          const result = findPositionAtTime(positions, newTime.getTime());
          if (result) {
            const interp = interpolatePosition(result.p1, result.p2, result.t);
            const newPos = Cartesian3.fromDegrees(
              interp.longitude,
              interp.latitude,
              interp.altitudeMSL,
            );
            currentPositionRef.current = newPos;

            // Update drone marker position + heading rotation
            const droneEntity = dsRef.current?.entities.getById('drone-marker');
            if (droneEntity) {
              droneEntity.position = newPos as any;
              if (droneEntity.billboard) {
                droneEntity.billboard.rotation = new ConstantProperty(
                  -CesiumMath.toRadians(interp.heading),
                ) as any;
              }
            }

            // Update operator marker if a newer pilot position is available
            // in the positions up to the current playback time
            const operatorEntity = dsRef.current?.entities.getById('operator-marker');
            const linkEntity = dsRef.current?.entities.getById('drone-pilot-link');
            let latestOp: { latitude: number; longitude: number; altitude?: number } | null = null;
            if (operatorEntity) {
              // Find most recent operator position up to current time
              for (let i = positions.length - 1; i >= 0; i--) {
                const pTime = new Date(positions[i].timestamp).getTime();
                if (pTime > newTime.getTime()) continue;
                const meta = positions[i].metadata;
                if (
                  meta &&
                  typeof meta.operatorLatitude === 'number' &&
                  typeof meta.operatorLongitude === 'number' &&
                  !(meta.operatorLatitude === 0 && meta.operatorLongitude === 0)
                ) {
                  latestOp = {
                    latitude: meta.operatorLatitude,
                    longitude: meta.operatorLongitude,
                    altitude: typeof meta.operatorAltitudeMSL === 'number' ? meta.operatorAltitudeMSL : undefined,
                  };
                  break;
                }
              }
              if (latestOp) {
                const pilotPos = Cartesian3.fromDegrees(
                  latestOp.longitude,
                  latestOp.latitude,
                  latestOp.altitude ?? 0,
                );
                operatorEntity.position = pilotPos as any;

                // Update drone-to-pilot connecting line
                if (linkEntity && linkEntity.polyline) {
                  linkEntity.polyline.positions = [newPos, pilotPos] as any;
                }
              }
            } else if (linkEntity && linkEntity.polyline) {
              // No operator entity — hide the link
              linkEntity.polyline.positions = [] as any;
            }

            publishReplayIconMarkers(interp, latestOp);

            // Update completed trail
            const trailEntity = dsRef.current?.entities.getById('completed-trail');
            if (trailEntity && trailEntity.polyline) {
              const trailPositions: Cartesian3[] = [];
              for (const p of positions) {
                const pTime = new Date(p.timestamp).getTime();
                if (pTime <= newTime.getTime()) {
                  trailPositions.push(
                    Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitudeMSL),
                  );
                } else {
                  break;
                }
              }
              trailPositions.push(newPos);
              trailEntity.polyline.positions = trailPositions as any;
            }
          }
        }
      } else {
        lastFrameRef.current = 0;

        // If not playing but playbackTime changed (scrubber), update position
        if (playbackTime) {
          const result = findPositionAtTime(positions, playbackTime.getTime());
          if (result) {
            const interp = interpolatePosition(result.p1, result.p2, result.t);
            const newPos = Cartesian3.fromDegrees(
              interp.longitude,
              interp.latitude,
              interp.altitudeMSL,
            );

            const droneEntity = dsRef.current?.entities.getById('drone-marker');
            if (droneEntity) {
              droneEntity.position = newPos as any;
              if (droneEntity.billboard) {
                droneEntity.billboard.rotation = new ConstantProperty(
                  -CesiumMath.toRadians(interp.heading),
                ) as any;
              }
            }

            // Update drone-to-pilot link when scrubbing
            const linkEntity = dsRef.current?.entities.getById('drone-pilot-link');
            const operatorEntity = dsRef.current?.entities.getById('operator-marker');
            if (linkEntity && linkEntity.polyline && operatorEntity && operatorEntity.position) {
              const opPos = operatorEntity.position.getValue(JulianDate.now());
              if (opPos) {
                linkEntity.polyline.positions = [newPos, opPos] as any;
              }
            }
            publishReplayIconMarkers(interp);
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastFrameRef.current = 0;
    };
  }, [publishReplayIconMarkers, viewer, visible, positions]);

  // Cleanup data source on unmount or when trackId becomes null
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (dsRef.current && viewer && !viewer.isDestroyed()) {
        viewer.dataSources.remove(dsRef.current, true);
        dsRef.current = null;
      }
      // Reset playback state
      const store = useMapStore.getState();
      store.setPlayback(false);
      store.setPlaybackTime(null);
      store.setTimeRange(null);
      store.setReplayIconMarkers([]);
      hasFlewToStartRef.current = false;
    };
  }, [viewer, trackId]);

  return {
    isLoading,
    error,
    duration,
    positionCount: positions.length,
  };
}
